import React, { useEffect, useState } from 'react';
import { Users, Search, Key, RefreshCw, X } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (searchTerm = '') => {
    setLoading(true);
    try {
      const data = await api.get(`/auth/admin/users?search=${searchTerm}`);
      if (data.success) {
        setUsers(data.data);
      }
    } catch (error) {
      toast.error('获取学员列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(search);
  };

  const openPasswordModal = (user) => {
    setPasswordModal(user);
    setNewPassword('');
  };

  const closePasswordModal = () => {
    setPasswordModal(null);
    setNewPassword('');
  };

  const handleResetPassword = async () => {
    if (!passwordModal || !newPassword || newPassword.length < 6) {
      toast.error('密码长度至少6位');
      return;
    }

    setResetting(true);
    try {
      const data = await api.post('/auth/admin-reset-password', {
        userId: passwordModal._id,
        newPassword
      });
      if (data.success) {
        toast.success('密码已重置，新密码：' + newPassword);
        closePasswordModal();
      }
    } catch (error) {
      toast.error(error.message || '重置失败');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">学员管理</h1>
        <span className="text-sm text-gray-500">共 {users.length} 名学员</span>
      </div>

      {/* 搜索框 */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索学员姓名、邮箱或手机号..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Search className="h-4 w-4" />
          搜索
        </button>
      </form>

      {/* 学员列表 */}
      <div className="card overflow-hidden">
        {users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">暂无学员</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">学员</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">邮箱</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">手机号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">注册时间</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="font-medium">{user.username}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{user.phone || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString('zh-CN')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => openPasswordModal(user)}
                      className="px-3 py-1 text-xs bg-orange-100 text-orange-700 hover:bg-orange-200 rounded transition-colors flex items-center gap-1 ml-auto"
                    >
                      <Key className="h-3 w-3" />
                      重置密码
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 重置密码弹窗 */}
      {passwordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">🔑 重置密码</h2>
              <button onClick={closePasswordModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
              <p className="text-gray-600">学员：<span className="font-medium text-gray-900">{passwordModal.username}</span></p>
              <p className="text-gray-600 mt-1">邮箱：<span className="font-medium text-gray-900">{passwordModal.email}</span></p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">新密码</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="请输入新密码（至少6位）"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1">密码重置后，学员可以使用新密码登录</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closePasswordModal}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetting || newPassword.length < 6}
                className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50"
              >
                {resetting ? '重置中...' : '✅ 确认重置'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
